/**
 * The topics for one breakout session: the microsite Schedule's slot picker
 * list, lifted out so the place sheet chooses the same way the schedule does.
 */
import type { ConferencePhase, FullnessState } from '../lib/conference';
import { fullness } from '../lib/conference';
import type { TopicView } from '../lib/views';
import { SpeakerChip } from './Avatar';
import { FullnessBadge } from './FullnessBadge';
import { TopicTitle } from './TopicTitle';

export function SlotChoices({
  topics,
  slot,
  pickedSlug,
  phase,
  editable,
  guestCanRegister,
  onChoose,
  onRegister,
}: {
  topics: TopicView[];
  slot: number;
  pickedSlug: string | undefined;
  phase: ConferencePhase;
  editable: boolean;
  guestCanRegister: boolean;
  onChoose: (topic: TopicView) => void;
  onRegister: () => void;
}) {
  return (
    <div className="flex flex-col gap-2.5">
      {topics.map((topic) => {
        const entry = topic.slots.find((s) => s.slot === slot);
        if (!entry) return null;
        const mine = pickedSlug === topic.slug;
        const state: FullnessState | null =
          entry.picked === null || phase === 'recap'
            ? null
            : fullness(entry.picked, entry.capacity);
        const blocked = state === 'full' && !mine;
        return (
          <div
            key={topic.slug}
            className={`flex flex-col gap-3 rounded-2xl border p-4 ${
              mine ? 'border-wm-sage-800 bg-wm-sage-100' : 'border-wm-sage-200 bg-wm-petal'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <SpeakerChip {...topic.speaker} />
              <div className="flex shrink-0 items-center gap-2">
                {mine ? null : <FullnessBadge state={state} />}
                {editable ? (
                  <button
                    onClick={() => onChoose(topic)}
                    disabled={mine || blocked}
                    className={`wm-lift rounded-full px-3.5 py-1.5 text-xs font-semibold ${
                      mine
                        ? 'bg-wm-sage-800 text-wm-cream'
                        : blocked
                          ? 'bg-wm-cream text-wm-sage-300 cursor-not-allowed'
                          : 'bg-wm-marigold text-wm-sage-950'
                    }`}
                  >
                    {mine ? 'Your pick' : blocked ? 'Full' : 'Choose'}
                  </button>
                ) : guestCanRegister && !blocked ? (
                  // Same button, different destination: a guest's
                  // Choose goes to registration, where choosing
                  // actually happens for her.
                  <button
                    type="button"
                    onClick={onRegister}
                    className="wm-lift bg-wm-marigold text-wm-sage-950 rounded-full px-3.5 py-1.5 text-xs font-semibold"
                  >
                    Choose
                  </button>
                ) : mine ? (
                  <span className="wm-badge bg-wm-sage-800 text-wm-cream">Your pick</span>
                ) : null}
              </div>
            </div>
            <div>
              <TopicTitle topic={topic} />
              {/* Further from the subtitle than the subtitle is from
                          the name, so the eye groups the two halves of the
                          title together and reads the blurb as the next thing
                          rather than a third line of the same thing. */}
              <p className="text-wm-sage-700 mt-2.5 text-sm">{topic.blurb}</p>
              <p className="text-wm-sage-500 mt-1 text-xs">
                {topic.panel ? 'Hosted panel · ' : ''}
                {topic.room ?? 'Room to be announced'}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
